import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useBreakpointDown } from '@/hooks/use-breakpoint';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import Modal from '@/components/common/modal';
import britestageLogo from '@/assets/britestage-logo.png';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';

/** Contributor path (questionnaire completed, org skipped); basic = minimal onboarding only */
export type OnboardingFinishVariant = 'contributor' | 'basic';

export interface OnboardingFinishFormProps {
  open: boolean;
  variant: OnboardingFinishVariant;
  onBack: () => void;
  onConfirm: () => void;
  logoSrc?: string;
  loading?: boolean;
}

const variantKeys: Record<
  OnboardingFinishVariant,
  { title: TranslationKey; description: TranslationKey; body: TranslationKey }
> = {
  contributor: {
    title: 'onBoarding.finishForm.contributor.title',
    description: 'onBoarding.finishForm.contributor.description',
    body: 'onBoarding.finishForm.contributor.body',
  },
  basic: {
    title: 'onBoarding.finishForm.basic.title',
    description: 'onBoarding.finishForm.basic.description',
    body: 'onBoarding.finishForm.basic.body',
  },
};

const OnboardingFinishForm: React.FC<OnboardingFinishFormProps> = ({
  open,
  variant,
  onBack,
  onConfirm,
  logoSrc = britestageLogo,
  loading = false,
}) => {
  const t = useTranslations();
  const isMobile = useBreakpointDown('sm');
  const keys = variantKeys[variant];

  return (
    <Modal
      open={open}
      hideCloseIcon
      title={t(keys.title)}
      description={t(keys.description)}
      size="md"
      fullScreen={isMobile}
      width={isMobile ? undefined : '450px'}
      height={isMobile ? undefined : '650px'}
      footer={
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onBack}
            startIcon={<ChevronLeftOutlinedIcon />}
            disabled={loading}
          >
            {t('common.back')}
          </Button>
          <Button variant="contained" color="info" onClick={onConfirm} disabled={loading}>
            {t('common.proceed')}
          </Button>
        </Box>
      }
    >
      <Box sx={{ textAlign: 'center', py: 2, maxHeight: '100%', overflow: 'auto' }}>
        <img
          src={logoSrc}
          alt="Britestage Logo"
          style={{ marginBottom: 24, width: '200px', height: '47px', objectFit: 'contain' }}
        />
        <Typography variant="body1" sx={{ mb: 2 }}>
          {t(keys.body)}
        </Typography>
      </Box>
    </Modal>
  );
};

export default OnboardingFinishForm;
